import { Box, Drawer, Stack } from "@mui/material";
import { MenuItem } from "Components/Layout/Menu/MenuItem";
import { UserHeader } from "Components/Layout/UserHeader/UserHeader";
import { PageTitleMobile } from "Components/Shared/PageTitles/PageTitleMobile";
import { useMenu } from "Hooks/Layout/useMenu";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { useCurrentUserHomepage } from "Hooks/useCurrentUserHomepage";
import type { AccessRoleCode } from "Models/AccessRoleCodes";
import type { BackNavigationConfig } from "Navigation/types";
import type { ApplicationLink } from "Types/types";
import { useState } from "react";
import { useLocation } from "react-router";
import styled from "styled-components";

const drawerWidth = 350;

const StyledWrapper = styled.div``;

const StyledMenuIconWrapper = styled(Box)<{ $isVisible: boolean }>`
  position: absolute;
  left: 5px;
  top: 10px;
  z-index: 1;

  ${(props) => props.theme.breakpoints.down("md")} {
    ${(props) =>
      !props.$isVisible &&
      `
      display: none;
    `}
  }
`;

const StyledMenu = styled.div`
  position: relative;
  top: 0;
  left: 0;
  width: ${drawerWidth}px;
  height: ${(props) => props.theme.vh(100)};
  background-color: ${(props) => props.theme.colors.gray};
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: auto;
  padding-left: ${(props) => props.theme.spacing(2)};
`;

const StyledList = styled.div`
  margin-top: ${(props) => props.theme.spacing(1)};
  display: flex;
  flex-direction: column;
`;

type Props = {
  isVisible: boolean;
  title: string;
  onChevronClick?: () => void;
  backNavigation: BackNavigationConfig;
};

const LinkItem = (props: ApplicationLink) => {
  const { pathname } = useLocation();
  const { homepageUrl } = useCurrentUserHomepage();

  if (props.emptySpaceSize) {
    return (
      <Box sx={{ height: `calc(var(--vh) * ${props.emptySpaceSize * 6})` }} />
    );
  }

  const forceIsActive = pathname === "/" && props.url === homepageUrl;

  return (
    <MenuItem item={props} pathname={pathname} forceIsActive={forceIsActive} />
  );
};

type MenuLinksProps = {
  userRole: AccessRoleCode;
};

const MenuLinks = ({ userRole }: MenuLinksProps) => {
  const { topMenu, bottomMenu } = useMenu(userRole);

  return (
    <StyledMenu>
      <Stack pt={5} gap={3} pb={6}>
        <UserHeader />
      </Stack>
      <StyledList>
        {topMenu.map((link, index) => (
          <LinkItem {...link} key={link.url || index} />
        ))}
      </StyledList>
      <StyledList>
        {bottomMenu.map((link, index) => (
          <LinkItem {...link} key={link.url || index} />
        ))}
      </StyledList>
    </StyledMenu>
  );
};

const Menu = ({ title, isVisible, onChevronClick, backNavigation }: Props) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const userRole = useCurrentUserRole();

  if (!userRole) {
    return null;
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const container =
    window !== undefined ? () => window.document.body : undefined;

  return (
    <StyledWrapper>
      <StyledMenuIconWrapper
        $isVisible={isVisible}
        sx={{ display: { sm: "block", md: "none" } }}
      >
        <PageTitleMobile
          title={title}
          backNavigation={backNavigation}
          onChevronClick={onChevronClick}
        />
      </StyledMenuIconWrapper>
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          border: "none",
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
          },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            border: "none",
            boxShadow: "none",
          },
        }}
      >
        <MenuLinks userRole={userRole} />
      </Drawer>
      <Drawer
        open
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          height: "100%",
          border: "none",
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            position: "unset",
            border: "none",
            boxShadow: "none",
            backgroundColor: "transparent",
          },
        }}
      >
        <MenuLinks userRole={userRole} />
      </Drawer>
    </StyledWrapper>
  );
};

export { Menu };
